import React, { useEffect, useState } from "react";
import { Bell, BellRing, BellOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  isPushSupported,
  getCurrentSubscription,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
} from "@/lib/pushClient";

export default function PushNotificationSettings() {
  const supported = isPushSupported();
  const [subscribed, setSubscribed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [working, setWorking] = useState(false);
  const [permission, setPermission] = useState(supported ? Notification.permission : "denied");

  useEffect(() => {
    let cancel = false;
    (async () => {
      if (!supported) {
        setChecking(false);
        return;
      }
      const sub = await getCurrentSubscription();
      if (!cancel) {
        setSubscribed(!!sub);
        setChecking(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [supported]);

  const enable = async () => {
    setWorking(true);
    try {
      await subscribeToPush();
      setSubscribed(true);
      setPermission("granted");
      toast.success("Notificaciones activadas en este dispositivo");
    } catch (e) {
      toast.error(e.message || "No se pudo activar");
      setPermission(typeof Notification !== "undefined" ? Notification.permission : "denied");
    } finally {
      setWorking(false);
    }
  };

  const disable = async () => {
    setWorking(true);
    try {
      await unsubscribeFromPush();
      setSubscribed(false);
      toast.success("Notificaciones desactivadas en este dispositivo");
    } catch (e) {
      toast.error(e.message || "Error al desactivar");
    } finally {
      setWorking(false);
    }
  };

  const test = async () => {
    setWorking(true);
    try {
      const res = await sendTestPush();
      toast.success(`Notificación de prueba enviada (${res.sent} dispositivo${res.sent === 1 ? "" : "s"})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar prueba");
    } finally {
      setWorking(false);
    }
  };

  if (!supported) {
    return (
      <div className="rounded-2xl border border-[#EADBCE] bg-[#FCF9F2] p-4" data-testid="push-unsupported">
        <div className="flex items-center gap-2 text-[#8A796F] text-sm font-semibold">
          <BellOff className="w-4 h-4" /> Notificaciones no soportadas
        </div>
        <p className="text-xs text-[#8A796F] mt-1">
          Instala la app en tu pantalla de inicio para recibir alertas push (iOS 16.4+ / Android / Chrome desktop).
        </p>
      </div>
    );
  }

  if (checking) {
    return (
      <div className="rounded-2xl border border-[#EADBCE] p-4 flex items-center gap-2 text-[#8A796F] text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Verificando…
      </div>
    );
  }

  const isBlocked = permission === "denied";

  return (
    <div className="rounded-2xl border border-[#EADBCE] p-4 space-y-3" data-testid="push-settings">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${subscribed ? "bg-[#A7E4D3]/40 text-[#2E8B75]" : "bg-[#FCF9F2] text-[#8A796F]"}`}>
          {subscribed ? <BellRing className="w-5 h-5" /> : <Bell className="w-5 h-5" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-[#4A3B32]">Notificaciones push</p>
          <p className="text-xs text-[#8A796F]">
            {subscribed
              ? "Este dispositivo está recibiendo alertas."
              : isBlocked
              ? "Permiso bloqueado en el navegador. Habilítalo en Ajustes → Notificaciones."
              : "Recibe alertas al instante cuando falta algo o baja un helado."}
          </p>
        </div>
      </div>

      <div className="flex gap-2">
        {subscribed ? (
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={disable}
              disabled={working}
              data-testid="push-disable-btn"
              className="rounded-full border-[#EADBCE] text-[#4A3B32] hover:bg-[#FCF9F2]"
            >
              {working ? <Loader2 className="w-4 h-4 animate-spin" /> : "Desactivar"}
            </Button>
            <Button
              size="sm"
              onClick={test}
              disabled={working}
              data-testid="push-test-btn"
              className="rounded-full bg-[#FF7878] hover:bg-[#FF5C5C] text-white font-bold"
            >
              Enviar prueba
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            onClick={enable}
            disabled={working || isBlocked}
            data-testid="push-enable-btn"
            className="rounded-full bg-[#FF7878] hover:bg-[#FF5C5C] text-white font-bold w-full"
          >
            {working ? <Loader2 className="w-4 h-4 animate-spin" /> : "Activar en este dispositivo"}
          </Button>
        )}
      </div>
    </div>
  );
}
